import React, { useMemo } from 'react';
import { noop } from 'lodash';
import { CREATIVE_TYPE, FORM_MODES } from 'modules/campaigns/config';
import { ALL_MEDIA_FILES, ONLY_IMAGE_FILES } from 'modules/campaigns/modules/creative-creator/config/fileTypeConfigs';
import { MIN_THUMBNAIL_SIZE_VALIDATION_ERROR_MESSAGE_ID } from 'modules/campaigns/modules/creative-creator/config/thumbnailValidationConsts';
import { isFileTypeImage } from 'modules/campaigns/modules/creative-creator/services/utils';
import { MediaDragAndDropContent } from 'modules/campaigns/modules/creative-editor/components/MediaDragAndDrop/MediaDragAndDropContent';
import { modifyExternalUrl } from 'modules/campaigns/services/utils';
import { useFormDataContext, useFormFieldValue } from 'modules/taboola-common-frontend-modules/formData';
import { transformImageItem, transformVideoItem } from '../../../utils/fetchedMediaTransformationUtils';
import { MediaDragAndDrop } from '../../MediaDragAndDrop/MediaDragAndDrop';
import { GALLERY_ITEM_SOURCES } from '../consts';
import styles from './uploadGalleryItem.module.scss';

export const UploadGalleryItem = ({
    onInitUpload,
    onLoadedMedia,
    onRemoveUpload,
    setUploadErrorMessageId,
    itemHeight: getItemHeight = noop,
    className,
}) => {
    const itemHeight = useMemo(() => getItemHeight(), [getItemHeight]);
    const { mode } = useFormDataContext();
    const { value: creativeType } = useFormFieldValue({ field: 'creativeType' });
    const isDuplicateMode = mode === FORM_MODES.DUPLICATE;

    const fileTypes = creativeType === CREATIVE_TYPE.IMAGE && !isDuplicateMode ? ONLY_IMAGE_FILES : ALL_MEDIA_FILES;
    const onDrop = async (mediaResolver, { fileType, mediaUploadSource }) => {
        const uploadId = onInitUpload();
        try {
            const { url, gifUrl, fallbackImageUrl } = await mediaResolver();
            const isImage = isFileTypeImage(fileType);

            if (url) {
                const uploadedIem = isImage
                    ? transformImageItem({ thumbnailUrl: url }, GALLERY_ITEM_SOURCES.MEDIA_LIBRARY)
                    : transformVideoItem(
                          {
                              videoUrl: url,
                              gifUrl: modifyExternalUrl(gifUrl),
                              thumbnailUrl: fallbackImageUrl,
                          },
                          mediaUploadSource
                      );
                onLoadedMedia(uploadId, uploadedIem);
            }
        } catch (e) {
            if (e.status === MIN_THUMBNAIL_SIZE_VALIDATION_ERROR_MESSAGE_ID) {
                setUploadErrorMessageId('creative.creator.thumbnails.fetching.thumbnailSizeTooSmall');
            } else if (e.messageCode) {
                setUploadErrorMessageId(e.messageCode);
            } else {
                setUploadErrorMessageId(`creative.creator.thumbnails.fetching.${e?.status}`);
            }
            onRemoveUpload(uploadId);
        }
    };

    return (
        <MediaDragAndDrop
            fileTypes={fileTypes}
            onDrop={onDrop}
            onFileTypeFailure={() => setUploadErrorMessageId('file.upload.server.error.format')}
            onBeforeFileProcessing={() => setUploadErrorMessageId(null)}
            className={className ? className : styles['drag-and-drop-container']}
        >
            <MediaDragAndDropContent itemHeight={itemHeight} />
        </MediaDragAndDrop>
    );
};
